import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ReturnItemModal from '../../components/returns/ReturnItemModal'
import {
  RotateCcw,
  Search,
  Filter,
  RefreshCw,
  Download,
  TrendingDown,
  DollarSign,
  Package,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  ChevronRight,
  ArrowLeft,
  Edit2
} from 'lucide-react'

interface Return {
  id: string
  rma_number: string
  customer_name: string
  customer_email: string
  status: string
  return_reason: string
  condition: string
  priority: string
  net_impact: number
  potential_future_revenue: number
  refund_amount: number
  tracking_number?: string
  expected_arrival_date?: string
  is_arriving_today: boolean
  is_overdue: boolean
  days_overdue: number
  item_count: number
  created_at: string
}

interface Stats {
  total_returns: number
  active_returns: number
  completed_returns: number
  cancelled_returns: number
  total_refunds_issued: number
  pending_refunds: number
  total_net_impact: number
  total_potential_revenue: number
  avg_processing_days: number
  total_restocking_fees: number
}

export default function ReturnsPage() {
  const navigate = useNavigate()
  const [returns, setReturns] = useState<Return[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [refreshing, setRefreshing] = useState(false)
  const [selectedReturn, setSelectedReturn] = useState<Return | null>(null)
  const [editingReturn, setEditingReturn] = useState<any>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const fetchReturns = async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter !== 'all') params.append('status', statusFilter)

      const response = await fetch(`/api/returns-pg?${params.toString()}`)
      const result = await response.json()

      if (result.success) {
        setReturns(result.data || [])
      }
    } catch (error) {
      console.error('Error fetching returns:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/returns-pg/stats')
      const result = await response.json()

      if (result.success) {
        setStats(result.data.summary)
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchReturns(), fetchStats()])
      setLoading(false)
    }
    loadData()
  }, [statusFilter])

  const handleRefresh = async () => {
    setRefreshing(true)
    await Promise.all([fetchReturns(), fetchStats()])
    setRefreshing(false)
  }

  // Helper function to safely format numbers from DB (which come as strings)
  const formatCurrency = (value: any): string => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    return (num || 0).toFixed(2)
  }

  const handleViewReturn = async (returnData: Return) => {
    try {
      // Fetch full return details with items and images
      const response = await fetch(`/api/returns-pg/${returnData.id}`)
      const result = await response.json()

      if (result.success && result.data) {
        console.log('Return details with images:', result.data)
        console.log('Images array:', result.data.images)
        setSelectedReturn(result.data)
      }
    } catch (error) {
      console.error('Error fetching return details:', error)
    }
  }

  const handleEditReturn = async (returnData: Return) => {
    try {
      // Fetch full return details with items
      const response = await fetch(`/api/returns-pg/${returnData.id}`)
      const result = await response.json()

      if (result.success && result.data) {
        setEditingReturn(result.data)
        setEditModalOpen(true)
        setSelectedReturn(null) // Close details modal
      }
    } catch (error) {
      console.error('Error fetching return details:', error)
    }
  }

  const handleEditSuccess = () => {
    setEditModalOpen(false)
    setEditingReturn(null)
    fetchReturns() // Refresh the list
  }

  const filteredReturns = returns.filter(ret => {
    const matchesSearch =
      ret.rma_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ret.tracking_number?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesPriority = priorityFilter === 'all' || ret.priority === priorityFilter

    return matchesSearch && matchesPriority
  })

  const statusColors: Record<string, string> = {
    pending: '#FF6B35',
    approved: '#FFD700',
    label_sent: '#00F0FF',
    in_transit: '#9D4EDD',
    received: '#00B8D4',
    inspecting: '#FF9500',
    accepted: '#00FF41',
    rejected: '#FF006E',
    restocking: '#06b6d4',
    refund_processing: '#f59e0b',
    completed: '#10b981',
    cancelled: '#6b7280'
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'approved':
        return <Clock style={{ width: 16, height: 16 }} />
      case 'in_transit':
      case 'label_sent':
        return <Truck style={{ width: 16, height: 16 }} />
      case 'completed':
      case 'accepted':
        return <CheckCircle style={{ width: 16, height: 16 }} />
      case 'rejected':
      case 'cancelled':
        return <XCircle style={{ width: 16, height: 16 }} />
      default:
        return <RotateCcw style={{ width: 16, height: 16 }} />
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e1a 0%, #1a1f2e 50%, #0a0e1a 100%)',
      padding: '24px',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Navigation Bar */}
      <div style={{
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          {/* Empty for alignment */}
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(138, 149, 170, 0.1)',
              border: '2px solid rgba(138, 149, 170, 0.4)',
              borderRadius: '6px',
              padding: '12px 20px',
              color: '#8A95AA',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <ArrowLeft style={{ width: 16, height: 16 }} />
            BACK
          </button>
        </div>
      </div>

      {/* Header */}
      <div style={{
        marginBottom: '32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #9D4EDD, #C77DFF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
            letterSpacing: '3px',
            margin: 0,
            textShadow: '0 0 40px rgba(157, 78, 221, 0.5)'
          }}>
            RETURNS MANAGEMENT
          </h1>
          <p style={{
            color: '#8A95AA',
            fontSize: '16px',
            margin: '8px 0 0 0'
          }}>
            Track and manage product returns, RMAs, and refunds
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            padding: '12px 24px',
            background: refreshing ? 'rgba(157, 78, 221, 0.3)' : 'linear-gradient(135deg, #9D4EDD, #7B2CBF)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontWeight: 700,
            fontSize: '14px',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            boxShadow: refreshing ? 'none' : '0 0 20px rgba(157, 78, 221, 0.4)'
          }}
        >
          <RefreshCw style={{ width: 16, height: 16, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          {/* Total Refunds Issued */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 0, 110, 0.1), rgba(255, 107, 53, 0.1))',
            border: '2px solid rgba(255, 0, 110, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 0 30px rgba(255, 0, 110, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <DollarSign style={{ width: 32, height: 32, color: '#FF006E' }} />
              <span style={{ color: '#FF006E', fontSize: '12px', fontWeight: 600 }}>ISSUED</span>
            </div>
            <div style={{ color: '#FF006E', fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
              ${formatCurrency(stats.total_refunds_issued)}
            </div>
            <div style={{ color: '#8A95AA', fontSize: '13px' }}>Total Refunds Issued</div>
          </div>

          {/* Pending Refunds */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1), rgba(255, 149, 0, 0.1))',
            border: '2px solid rgba(255, 215, 0, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 0 30px rgba(255, 215, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <Clock style={{ width: 32, height: 32, color: '#FFD700' }} />
              <span style={{ color: '#FFD700', fontSize: '12px', fontWeight: 600 }}>PENDING</span>
            </div>
            <div style={{ color: '#FFD700', fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
              ${formatCurrency(stats.pending_refunds)}
            </div>
            <div style={{ color: '#8A95AA', fontSize: '13px' }}>Pending Refunds</div>
          </div>

          {/* Net Impact */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(251, 146, 60, 0.1))',
            border: '2px solid rgba(255, 107, 53, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 0 30px rgba(255, 107, 53, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <TrendingDown style={{ width: 32, height: 32, color: '#FF6B35' }} />
              <span style={{ color: '#FF6B35', fontSize: '12px', fontWeight: 600 }}>LOSS</span>
            </div>
            <div style={{ color: '#FF6B35', fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
              ${formatCurrency(Math.abs(stats.total_net_impact))}
            </div>
            <div style={{ color: '#8A95AA', fontSize: '13px' }}>Total Net Impact</div>
          </div>

          {/* Potential Recovery */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(0, 184, 212, 0.1))',
            border: '2px solid rgba(0, 240, 255, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 0 30px rgba(0, 240, 255, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <Package style={{ width: 32, height: 32, color: '#00F0FF' }} />
              <span style={{ color: '#00F0FF', fontSize: '12px', fontWeight: 600 }}>RECOVERY</span>
            </div>
            <div style={{ color: '#00F0FF', fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
              ${formatCurrency(stats.total_potential_revenue)}
            </div>
            <div style={{ color: '#8A95AA', fontSize: '13px' }}>Potential Future Revenue</div>
          </div>

          {/* Active Returns */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.1), rgba(123, 44, 191, 0.1))',
            border: '2px solid rgba(157, 78, 221, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 0 30px rgba(157, 78, 221, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <RotateCcw style={{ width: 32, height: 32, color: '#9D4EDD' }} />
              <span style={{ color: '#9D4EDD', fontSize: '12px', fontWeight: 600 }}>ACTIVE</span>
            </div>
            <div style={{ color: '#9D4EDD', fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
              {stats.active_returns}
            </div>
            <div style={{ color: '#8A95AA', fontSize: '13px' }}>Active Returns</div>
          </div>

          {/* Average Processing Time */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(79, 70, 229, 0.1))',
            border: '2px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 0 30px rgba(99, 102, 241, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <Clock style={{ width: 32, height: 32, color: '#6366f1' }} />
              <span style={{ color: '#6366f1', fontSize: '12px', fontWeight: 600 }}>AVG TIME</span>
            </div>
            <div style={{ color: '#6366f1', fontSize: '32px', fontWeight: 700, marginBottom: '4px' }}>
              {stats.avg_processing_days} days
            </div>
            <div style={{ color: '#8A95AA', fontSize: '13px' }}>Average Processing Time</div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div style={{
        background: 'rgba(26, 31, 46, 0.6)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(157, 78, 221, 0.2)',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '16px', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 20,
              height: 20,
              color: '#8A95AA'
            }} />
            <input
              type="text"
              placeholder="Search by RMA, customer, email, tracking..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(157, 78, 221, 0.3)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                fontFamily: 'Rajdhani, sans-serif'
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(157, 78, 221, 0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 600
            }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="in_transit">In Transit</option>
            <option value="received">Received</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* Priority Filter */}
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(157, 78, 221, 0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '14px',
              fontFamily: 'Rajdhani, sans-serif',
              fontWeight: 600
            }}
          >
            <option value="all">All Priorities</option>
            <option value="STA">Standard</option>
            <option value="EXP">Expedited</option>
            <option value="VIP">VIP</option>
          </select>

          {/* Export Button */}
          <button
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #00F0FF, #00B8D4)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 700,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
            }}
          >
            <Download style={{ width: 16, height: 16 }} />
            Export
          </button>
        </div>
      </div>

      {/* Returns List */}
      <div style={{
        background: 'rgba(26, 31, 46, 0.6)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(157, 78, 221, 0.2)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '180px 220px 1fr 150px 120px 120px 120px 150px 80px',
          gap: '16px',
          padding: '16px 24px',
          background: 'linear-gradient(135deg, rgba(157, 78, 221, 0.2), rgba(123, 44, 191, 0.1))',
          borderBottom: '2px solid rgba(157, 78, 221, 0.3)',
          color: '#C4B5FD',
          fontSize: '12px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}>
          <div>RMA Number</div>
          <div>Item Name/Title</div>
          <div>Status</div>
          <div>Marketplace</div>
          <div>Reason</div>
          <div>Priority</div>
          <div>Net Impact</div>
          <div>Potential Recovery</div>
          <div></div>
        </div>

        {/* Table Body */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#8A95AA' }}>
            <RefreshCw style={{ width: 48, height: 48, margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
            <p>Loading returns...</p>
          </div>
        ) : filteredReturns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#8A95AA' }}>
            <RotateCcw style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
            <p>{searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' ? 'No returns match your filters' : 'No returns yet'}</p>
          </div>
        ) : (
          filteredReturns.map((returnItem, index) => (
            <div
              key={returnItem.id}
              onClick={() => handleViewReturn(returnItem)}
              style={{
                display: 'grid',
                gridTemplateColumns: '180px 220px 1fr 150px 120px 120px 120px 150px 80px',
                gap: '16px',
                padding: '20px 24px',
                borderBottom: index < filteredReturns.length - 1 ? '1px solid rgba(157, 78, 221, 0.1)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: returnItem.is_overdue ? 'rgba(255, 0, 110, 0.05)' : returnItem.is_arriving_today ? 'rgba(255, 215, 0, 0.05)' : 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(157, 78, 221, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = returnItem.is_overdue ? 'rgba(255, 0, 110, 0.05)' : returnItem.is_arriving_today ? 'rgba(255, 215, 0, 0.05)' : 'transparent'
              }}
            >
              {/* RMA Number */}
              <div style={{ color: '#9D4EDD', fontWeight: 700, fontSize: '14px' }}>
                {returnItem.rma_number}
                {returnItem.is_overdue && (
                  <div style={{ color: '#FF006E', fontSize: '11px', marginTop: '4px' }}>
                    OVERDUE {returnItem.days_overdue}d
                  </div>
                )}
                {returnItem.is_arriving_today && !returnItem.is_overdue && (
                  <div style={{ color: '#FFD700', fontSize: '11px', marginTop: '4px' }}>
                    ARRIVING TODAY
                  </div>
                )}
              </div>

              {/* Item Name/Title */}
              <div>
                {returnItem.items && returnItem.items.length > 0 ? (
                  returnItem.items.map((item: any, idx: number) => (
                    <div key={idx} style={{ marginBottom: idx < returnItem.items.length - 1 ? '8px' : '0' }}>
                      {item.item_name && (
                        <div style={{ color: 'white', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                          {item.item_name}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {item.item_ic && (
                          <div style={{ color: '#00F0FF', fontSize: '11px', fontWeight: 500 }}>
                            IC: {item.item_ic}
                          </div>
                        )}
                        {item.item_sku && (
                          <div style={{ color: '#8A95AA', fontSize: '11px' }}>
                            SKU: {item.item_sku}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>No items</div>
                )}
              </div>

              {/* Status */}
              <div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 12px',
                  background: `${statusColors[returnItem.status] || '#6b7280'}22`,
                  border: `1px solid ${statusColors[returnItem.status] || '#6b7280'}`,
                  borderRadius: '6px',
                  color: statusColors[returnItem.status] || '#6b7280',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase'
                }}>
                  {getStatusIcon(returnItem.status)}
                  {returnItem.status.replace('_', ' ')}
                </div>
              </div>

              {/* Marketplace */}
              <div style={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>
                {returnItem.marketplace || 'N/A'}
              </div>

              {/* Reason */}
              <div style={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>
                {returnItem.return_reason}
              </div>

              {/* Priority */}
              <div>
                <span style={{
                  padding: '4px 8px',
                  background: returnItem.priority === 'VIP' ? 'rgba(255, 215, 0, 0.2)' : returnItem.priority === 'EXP' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(138, 149, 170, 0.2)',
                  border: `1px solid ${returnItem.priority === 'VIP' ? '#FFD700' : returnItem.priority === 'EXP' ? '#FF6B35' : '#8A95AA'}`,
                  borderRadius: '4px',
                  color: returnItem.priority === 'VIP' ? '#FFD700' : returnItem.priority === 'EXP' ? '#FF6B35' : '#8A95AA',
                  fontSize: '11px',
                  fontWeight: 700
                }}>
                  {returnItem.priority}
                </span>
              </div>

              {/* Net Impact */}
              <div style={{
                color: returnItem.net_impact < 0 ? '#FF006E' : '#00FF41',
                fontSize: '16px',
                fontWeight: 700
              }}>
                ${formatCurrency(Math.abs(returnItem.net_impact))}
              </div>

              {/* Potential Recovery */}
              <div style={{
                color: '#00F0FF',
                fontSize: '16px',
                fontWeight: 700
              }}>
                ${formatCurrency(returnItem.potential_future_revenue)}
              </div>

              {/* Action */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleEditReturn(returnItem)
                  }}
                  style={{
                    background: 'rgba(0, 240, 255, 0.1)',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    borderRadius: '4px',
                    padding: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                    e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.5)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                    e.currentTarget.style.borderColor = 'rgba(0, 240, 255, 0.3)'
                  }}
                >
                  <Edit2 style={{ width: 14, height: 14, color: '#00F0FF' }} />
                </button>
                <ChevronRight style={{ width: 20, height: 20, color: '#9D4EDD' }} />
              </div>
            </div>
          ))
        )}
      </div>

      {/* Return Details Modal */}
      {selectedReturn && (
        <div
          onClick={() => setSelectedReturn(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
              border: '1px solid rgba(157, 78, 221, 0.3)',
              borderRadius: '16px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '32px',
              boxShadow: '0 0 50px rgba(157, 78, 221, 0.3)'
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ color: '#00F0FF', fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
                Return Details
              </h2>
              <p style={{ color: '#9D4EDD', fontSize: '18px', fontWeight: '600' }}>
                {selectedReturn.rma_number}
              </p>
            </div>

            {/* Details Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div>
                <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Status</p>
                <p style={{ color: statusColors[selectedReturn.status] || '#fff', fontWeight: '600' }}>
                  {selectedReturn.status.toUpperCase()}
                </p>
              </div>
              <div>
                <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Priority</p>
                <p style={{ color: '#fff', fontWeight: '600' }}>{selectedReturn.priority}</p>
              </div>
              <div>
                <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Reason</p>
                <p style={{ color: '#fff', fontWeight: '600' }}>{selectedReturn.return_reason}</p>
              </div>
              <div>
                <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Condition</p>
                <p style={{ color: '#fff', fontWeight: '600' }}>{selectedReturn.condition}</p>
              </div>
              {selectedReturn.customer_name && (
                <div>
                  <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Customer</p>
                  <p style={{ color: '#fff', fontWeight: '600' }}>{selectedReturn.customer_name}</p>
                </div>
              )}
              {selectedReturn.customer_email && (
                <div>
                  <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Email</p>
                  <p style={{ color: '#fff', fontWeight: '600' }}>{selectedReturn.customer_email}</p>
                </div>
              )}
              {selectedReturn.tracking_number && (
                <div>
                  <p style={{ color: '#888', fontSize: '12px', marginBottom: '4px' }}>Tracking</p>
                  <p style={{ color: '#00F0FF', fontWeight: '600' }}>{selectedReturn.tracking_number}</p>
                </div>
              )}
            </div>

            {/* Financial Summary */}
            <div style={{
              background: 'rgba(157, 78, 221, 0.1)',
              border: '1px solid rgba(157, 78, 221, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <h3 style={{ color: '#9D4EDD', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                Financial Summary
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <p style={{ color: '#888', fontSize: '12px' }}>Refund Amount</p>
                  <p style={{ color: '#FF006E', fontSize: '18px', fontWeight: 'bold' }}>
                    ${formatCurrency(selectedReturn.refund_amount)}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '12px' }}>Net Impact</p>
                  <p style={{ color: '#FF006E', fontSize: '18px', fontWeight: 'bold' }}>
                    -${formatCurrency(Math.abs(selectedReturn.net_impact))}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '12px' }}>Potential Revenue</p>
                  <p style={{ color: '#00FF41', fontSize: '18px', fontWeight: 'bold' }}>
                    ${formatCurrency(selectedReturn.potential_future_revenue)}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '12px' }}>Item Count</p>
                  <p style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>
                    {selectedReturn.item_count}
                  </p>
                </div>
              </div>
            </div>

            {/* Return Images */}
            {selectedReturn.images && selectedReturn.images.length > 0 && (
              <div style={{
                background: 'rgba(0, 240, 255, 0.05)',
                border: '1px solid rgba(0, 240, 255, 0.2)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <h3 style={{ color: '#00F0FF', fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                  Return Images ({selectedReturn.images.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: '12px'
                }}>
                  {selectedReturn.images.map((image: any) => (
                    <div
                      key={image.id}
                      style={{
                        position: 'relative',
                        aspectRatio: '1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        border: '1px solid rgba(0, 240, 255, 0.3)',
                        cursor: 'pointer'
                      }}
                      onClick={() => window.open(image.image_url, '_blank')}
                    >
                      <img
                        src={image.image_url}
                        alt={image.description || 'Return image'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      {image.image_type && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'rgba(0, 0, 0, 0.8)',
                          padding: '4px 8px',
                          fontSize: '11px',
                          color: '#00F0FF',
                          textTransform: 'uppercase'
                        }}>
                          {image.image_type}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => handleEditReturn(selectedReturn)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #00F0FF 0%, #00B8D4 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#0a0a0a',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Edit Return
              </button>
              <button
                onClick={() => setSelectedReturn(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #9D4EDD 0%, #7B2CBF 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Return Modal */}
      {editingReturn && editingReturn.items && editingReturn.items[0] && (
        <ReturnItemModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false)
            setEditingReturn(null)
          }}
          item={{
            id: editingReturn.items[0].inventory_item_id,
            name: editingReturn.items[0].item_name,
            sku: editingReturn.items[0].item_sku
          }}
          existingReturn={editingReturn}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  )
}
